import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { securityReviewApi } from '@/api/securityReview'
import { getErrorMessage, shouldNotRetryRequest } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { DEFAULT_QUERY_RETRY_COUNT } from '@/lib/queryClient'
import { useTenantKey } from './useTenantKey'
import type { PaginationParams, SetSecurityReviewFindingDecisionRequest } from '@/types'

export function useSecurityReviewRun(projectId: string, workflowRunId: string | null) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'security-review', 'workflow-runs', workflowRunId!),
    queryFn: () => securityReviewApi.getRunByWorkflowRunId(projectId, workflowRunId!),
    enabled: !!projectId && !!workflowRunId,
    staleTime: 0,
    retry: (failureCount, error) => !shouldNotRetryRequest(error) && failureCount < DEFAULT_QUERY_RETRY_COUNT,
  })
}

export function useSecurityReviewFindings(
  projectId: string,
  securityReviewRunId: string | null,
  pagination?: PaginationParams,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) {
  const tenantKey = useTenantKey()
  const params = { ...pagination, sortBy, sortDirection }

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'security-review', 'runs', securityReviewRunId!, 'findings', params),
    queryFn: () => securityReviewApi.getFindings(projectId, securityReviewRunId!, params),
    enabled: !!projectId && !!securityReviewRunId,
    staleTime: 0,
    retry: (failureCount, error) => !shouldNotRetryRequest(error) && failureCount < DEFAULT_QUERY_RETRY_COUNT,
  })
}

export function useSecurityReviewApprovalBlock(projectId: string, runId: string | null) {
  const { data: securityReviewRun, isLoading: isRunLoading } = useSecurityReviewRun(projectId, runId)
  const isSecurityReviewGate = securityReviewRun?.status === 'AwaitingReview'
  const hasSecurityFindings = isSecurityReviewGate && securityReviewRun.totalFindingsCount > 0

  const { data: securityReviewFindings, isLoading: isFindingsLoading, isError: isFindingsError } = useSecurityReviewFindings(
    projectId,
    hasSecurityFindings ? securityReviewRun.id : null,
    hasSecurityFindings ? { maxResultCount: securityReviewRun.totalFindingsCount } : undefined,
  )

  const pendingFindingsCount = securityReviewFindings?.items.filter((f) => f.decision === 'Pending').length ?? 0
  const isLoading = isRunLoading || (hasSecurityFindings && (isFindingsLoading || isFindingsError))
  const hasUndecidedFindings = hasSecurityFindings && pendingFindingsCount > 0

  return {
    disabled: isLoading || hasUndecidedFindings,
    reason: isLoading
      ? undefined
      : hasUndecidedFindings
        ? `${pendingFindingsCount} finding(s) still need a decision`
        : undefined,
    pendingFindingsCount,
    totalFindingsCount: securityReviewRun?.totalFindingsCount ?? 0,
  }
}

export function useSetSecurityReviewFindingDecision(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ findingId, data }: { findingId: string; data: SetSecurityReviewFindingDecisionRequest }) =>
      securityReviewApi.setFindingDecision(projectId, findingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'security-review') })
    },
    onError: (error) => {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Could not record this decision. Please try again.')
    },
  })
}